import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type {
  PartnerCampaignOverviewRow,
  PartnerSenderHealthRow,
  PartnerMemberQuotaRow,
} from '@/hooks/useEnterpriseEmailMonitoring';
import type { PartnerEmulationActivityRow } from '@/hooks/useEnterpriseEmulationActivity';

/**
 * M3 (#573): single at-a-glance triage line for the enterprise dashboard.
 *
 * Sums the four alert sources into one count and renders either a destructive
 * "N issues need attention" or a calm-green "All clear". The count function is
 * pure + exported so it can be unit-tested across mixed/empty inputs.
 */

export interface HealthStatusInputs {
  campaigns: PartnerCampaignOverviewRow[];
  senders: PartnerSenderHealthRow[];
  quotas: PartnerMemberQuotaRow[];
  emulation: PartnerEmulationActivityRow[];
}

/**
 * Count the issues that need attention across all four alert sources:
 * campaigns flagged `needs_attention`, senders that are not `connected`,
 * quotas at/near the daily wall, and security-forced emulation closures.
 */
export function countHealthIssues(inputs: HealthStatusInputs): number {
  const campaignIssues = inputs.campaigns.filter((c) => c.needs_attention).length;
  const senderIssues = inputs.senders.filter(
    (s) => s.health_status !== 'connected',
  ).length;
  const quotaIssues = inputs.quotas.filter((q) => q.near_wall).length;
  const emulationIssues = inputs.emulation.filter((e) => e.forced_by_security).length;
  return campaignIssues + senderIssues + quotaIssues + emulationIssues;
}

interface HealthStatusLineProps extends HealthStatusInputs {
  /** True while any of the underlying queries is still loading. */
  isLoading?: boolean;
}

export function HealthStatusLine({
  isLoading,
  ...inputs
}: HealthStatusLineProps): JSX.Element {
  // While data is still arriving, stay neutral — don't flash "All clear".
  if (isLoading) {
    return (
      <Card className="flex items-center gap-3 p-4 text-muted-foreground">
        <span className="h-5 w-5 animate-pulse rounded-full bg-muted" />
        <span className="text-sm">Checking workspace health…</span>
      </Card>
    );
  }

  const count = countHealthIssues(inputs);

  if (count === 0) {
    return (
      <Card className="flex items-center gap-3 p-4">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-green-500" />
        <span className="text-sm font-medium text-green-700">All clear</span>
        <span className="text-sm text-muted-foreground">
          Nothing needs attention across these workspaces.
        </span>
      </Card>
    );
  }

  return (
    <Card className="flex items-center gap-3 border-destructive/40 bg-destructive/5 p-4">
      <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
      <span className="text-sm font-semibold text-destructive">
        {count} {count === 1 ? 'issue needs' : 'issues need'} attention
      </span>
      <span className="text-sm text-muted-foreground">
        Erroring campaigns, disconnected senders, quota limits, or security-forced
        emulation exits.
      </span>
    </Card>
  );
}
